import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { rehabAPI } from '../lib/api';
import { BTN, INPUT, Header, NotForYou, newId } from './Gym';
import RehabCharts, { Sparkline, PAIN_SERIES } from './rehabCharts';

/**
 * Finger rehab, at /gym/rehab: is a 4-week block working? Training is
 * pain-anchored (hold the discomfort at a target, let the Tindeq measure the
 * force), so the log is pain scores, forces at that pain, and right ÷ left
 * strength ratios.
 *
 *   Daily log      once a day, under a minute: sliders you tap or drag.
 *   Session        a rehab exercise: force at the target pain, working sets.
 *   Strength test  weekly, both hands together, right ÷ left as a %.
 *   Blocks         one variable changed for 28 days, then a summary.
 *
 * The sums are done on the server (services/rehab/analysis.js) and come with
 * the data, so this file only shows them. Each person sees only their own.
 */

export const TEST_TYPES = [
  { test: 'mono', grip: 'half', label: 'Mono, half crimp' },
  { test: 'mono', grip: 'drag', label: 'Mono, drag' },
  { test: 'mono', grip: 'intermediate', label: 'Mono, intermediate' },
  { test: 'whole', grip: 'half', label: 'Whole hand, half crimp' },
  { test: 'whole', grip: 'open', label: 'Whole hand, open hand' },
];
export const testLabel = (test, grip) => TEST_TYPES.find(t => t.test === test && t.grip === grip)?.label || `${test} ${grip}`;
const GRIPS = [['drag', 'Drag'], ['intermediate', 'Intermediate'], ['half', 'Half crimp']];
export const gripLabel = (g) => GRIPS.find(([k]) => k === g)?.[1] || (g === 'open' ? 'Open hand' : g || '');

const pad = (n) => String(n).padStart(2, '0');
export const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
export const addDays = (date, n) => new Date(Date.parse(`${date}T00:00:00Z`) + n * 864e5).toISOString().slice(0, 10);
export const fmtDay = (date, opts = { weekday: 'short', day: 'numeric', month: 'short' }) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, opts);
const num = (v) => (v == null ? '' : String(Number(v)));
const one = (v) => (v == null ? null : Math.round(v * 10) / 10);

// --- Data ---------------------------------------------------------------------------

const RehabContext = createContext(null);
export const useRehab = () => useContext(RehabContext);

function RehabProvider({ children }) {
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [failed, setFailed] = useState(false);
  const reload = useCallback(() => rehabAPI.all(localToday())
    .then(r => { setData(r.data); setFailed(false); })
    .catch(err => { if (err.response?.status === 404) setForbidden(true); else setFailed(true); }), []);
  useEffect(() => { reload(); }, [reload]);
  if (forbidden) return <NotForYou />;
  if (failed && !data) return <div className="pt-24 text-center text-gray-500">Couldn't load your rehab log. <button onClick={reload} className="text-green-400 underline">Try again</button></div>;
  if (!data) return <p className="pt-10 text-gray-500">Loading…</p>;
  return <RehabContext.Provider value={{ data, reload }}>{children}</RehabContext.Provider>;
}

export default function Rehab() {
  return (
    <RehabProvider>
      <Routes>
        <Route index element={<RehabHome />} />
        <Route path="day" element={<DayLog />} />
        <Route path="day/:date" element={<DayLog />} />
        <Route path="session/new" element={<SessionLog />} />
        <Route path="session/:id" element={<SessionLog />} />
        <Route path="test/new" element={<TestLog />} />
        <Route path="test/:id" element={<TestLog />} />
        <Route path="blocks" element={<Blocks />} />
        <Route path="blocks/new" element={<BlockForm />} />
        <Route path="blocks/:id" element={<BlockPage />} />
        <Route path="charts" element={<RehabCharts />} />
        <Route path="more" element={<More />} />
        <Route path="*" element={<Navigate to="/gym/rehab" replace />} />
      </Routes>
    </RehabProvider>
  );
}

// --- Inputs -------------------------------------------------------------------------

/**
 * A 0 to 10 slider made for a thumb: tap a number or drag along the row.
 * Unset shows "–". `ghost` marks the last value logged, for reference.
 */
export function PainSlider({ label, hint, value, onChange, ghost, optional, compact }) {
  const track = useRef(null);
  const pressed = useRef(false);
  const pick = (e) => {
    const box = track.current.getBoundingClientRect();
    const i = Math.max(0, Math.min(10, Math.floor(((e.clientX - box.left) / box.width) * 11)));
    if (i !== value) { onChange(i); try { navigator.vibrate?.(5); } catch { /* not on iPhone */ } }
  };
  return (
    <div className={compact ? '' : 'py-1.5'}>
      <div className="flex items-baseline gap-2 mb-1">
        <span className={`${compact ? 'text-xs text-gray-400' : 'text-sm font-semibold'} flex-1`}>{label}{hint && <span className="text-xs text-gray-500 font-normal"> {hint}</span>}</span>
        {optional && value != null && <button type="button" onClick={() => onChange(null)} className="text-xs text-gray-500 px-1">clear</button>}
        <span className={`${compact ? 'text-base' : 'text-2xl'} font-bold tabular-nums w-8 text-right ${value == null ? 'text-gray-600' : 'text-white'}`}>{value ?? '–'}</span>
      </div>
      <div
        ref={track}
        role="slider"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={10}
        aria-valuenow={value ?? undefined}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') onChange(Math.min(10, (value ?? -1) + 1));
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') onChange(Math.max(0, (value ?? 1) - 1));
        }}
        onPointerDown={(e) => { pressed.current = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* drag still works over the row */ } pick(e); }}
        onPointerMove={(e) => { if (pressed.current) pick(e); }}
        onPointerUp={() => { pressed.current = false; }}
        onPointerCancel={() => { pressed.current = false; }}
        className="flex gap-[3px] select-none touch-pan-y cursor-pointer"
      >
        {Array.from({ length: 11 }, (_, i) => {
          const on = value != null && i <= value;
          return (
            <div key={i} className={`relative flex-1 ${compact ? 'h-8' : 'h-11'} rounded-md flex items-center justify-center text-xs tabular-nums transition-colors ${i === value ? 'bg-orange-500 text-black font-bold' : on ? 'bg-orange-500/35 text-orange-100' : 'bg-[#1c1c1c] text-gray-500'}`}>
              {i}
              {ghost === i && i !== value && <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-gray-400" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A number box that keeps what is typed ("8." stays "8.") and gives back one decimal. */
export function NumberInput({ value, onChange, placeholder = '', suffix, className = '', decimals = 1 }) {
  const [text, setText] = useState(null);
  const shown = text ?? num(value);
  return (
    <div className="relative">
      <input
        inputMode="decimal"
        value={shown}
        placeholder={placeholder}
        onFocus={() => setText(num(value))}
        onBlur={() => setText(null)}
        onChange={(e) => {
          const clean = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');
          setText(clean);
          const n = Number(clean);
          onChange(clean === '' || clean === '.' || !Number.isFinite(n) ? null : Math.round(n * 10 ** decimals) / 10 ** decimals);
        }}
        className={`${INPUT} ${suffix ? 'pr-8' : ''} tabular-nums ${className}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">{suffix}</span>}
    </div>
  );
}

export function Segmented({ options, value, onChange, size = 'md' }) {
  return (
    <div className="flex rounded-xl bg-[#141414] border border-[#2a2a2a] p-0.5">
      {options.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className={`flex-1 rounded-lg ${size === 'sm' ? 'py-1.5 text-xs' : 'py-2.5 text-sm'} font-semibold ${value === k ? 'bg-[#2a2a2a] text-white' : 'text-gray-400'}`}>{label}</button>
      ))}
    </div>
  );
}

function Label({ children, right }) {
  return <div className="flex items-baseline mb-1"><span className="text-[11px] uppercase tracking-wide text-gray-500 flex-1">{children}</span>{right}</div>;
}

function Card({ children, className = '' }) {
  return <div className={`rounded-2xl bg-[#141414] border border-[#232323] p-4 ${className}`}>{children}</div>;
}

/** Save at the foot of the screen, always in reach of a thumb. */
function SaveBar({ onSave, busy, label = 'Save', disabled, error, children }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a] to-transparent pt-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="max-w-xl mx-auto px-4">
        {error && <p className="text-sm text-red-400 mb-2">{error}</p>}
        <div className="flex gap-2">
          {children}
          <button onClick={onSave} disabled={busy || disabled} className={`${BTN} flex-1 bg-green-500 text-black`}>{busy ? 'Saving…' : label}</button>
        </div>
      </div>
    </div>
  );
}

const failText = (err) => err?.response?.data?.error || (err?.response ? 'Couldn\'t save that. Try again.' : 'No signal. Try again when you have some.');

// --- Home ---------------------------------------------------------------------------

function RehabHome() {
  const { data } = useRehab();
  const a = data.analysis;
  const today = a.today;
  const block = a.block;
  const last7 = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));
  const byDate = Object.fromEntries(data.days.map(d => [d.date, d]));
  const recent = [
    ...data.sessions.map(s => ({ kind: 'session', id: s.id, date: s.date, at: s.created_at, title: s.exercise, sub: [s.hand === 'right' ? 'Right' : 'Left', s.grip && gripLabel(s.grip), s.force_kg != null && `${num(s.force_kg)} kg at ${s.target_pain}/10`].filter(Boolean).join(' · ') })),
    ...data.tests.map(t => ({ kind: 'test', id: t.id, date: t.date, at: t.created_at, title: 'Strength test', sub: (t.results || []).map(r => testLabel(r.test, r.grip)).join(', ') })),
  ].sort((x, y) => (x.date === y.date ? String(y.at).localeCompare(String(x.at)) : y.date.localeCompare(x.date))).slice(0, 6);

  return (
    <>
      <Header title="Rehab" back="/gym" right={<Link to="/gym/rehab/charts" className="text-sm text-gray-400 px-2 py-1">Charts</Link>} />

      {a.back_off?.flag && (
        <div className="rounded-2xl bg-amber-500/15 border border-amber-500/60 p-4 mb-3" role="alert">
          <div className="font-bold text-amber-300">⚠️ Back off 10 to 15% next session</div>
          <div className="text-sm text-amber-100/80 mt-0.5">Stiffness this morning is {a.back_off.stiffness}/10, {num(a.back_off.over)} above your 7-day average of {num(a.back_off.average)}.</div>
        </div>
      )}

      <Card className="mb-3">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1">
            <div className="text-sm text-gray-400">{fmtDay(today, { weekday: 'long', day: 'numeric', month: 'long' })}</div>
            <div className={`font-semibold ${a.logged_today ? 'text-green-400' : 'text-white'}`}>{a.logged_today ? '✓ Logged today' : 'Not logged yet today'}</div>
          </div>
        </div>
        <Link to={`/gym/rehab/day/${today}`} className={`${BTN} block text-center text-lg ${a.logged_today ? 'bg-[#1f1f1f] text-white' : 'bg-green-500 text-black py-4'}`}>{a.logged_today ? 'Edit today' : 'Log today'}</Link>
        <div className="grid grid-cols-7 gap-1 mt-3">
          {last7.map(d => {
            const e = byDate[d];
            return (
              <Link key={d} to={`/gym/rehab/day/${d}`} className={`rounded-lg py-1.5 text-center ${d === today ? 'ring-1 ring-green-700' : ''} ${e ? 'bg-[#1f1f1f]' : 'bg-[#101010]'}`} aria-label={`${fmtDay(d)}${e ? `, finger pain ${e.rest_pain ?? 'not given'}` : ', not logged'}`}>
                <div className="text-[10px] text-gray-500">{fmtDay(d, { weekday: 'narrow' })}</div>
                <div className={`text-sm font-semibold tabular-nums ${e ? 'text-white' : 'text-gray-700'}`}>{e ? (e.rest_pain ?? '·') : '–'}</div>
              </Link>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-600 mt-1">Finger pain at rest, last 7 days. Tap a day to fill it in or change it.</p>
      </Card>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <Link to="/gym/rehab/session/new" className={`${BTN} text-center bg-[#1f1f1f] text-white`}>+ Session</Link>
        <Link to="/gym/rehab/test/new" className={`${BTN} text-center bg-[#1f1f1f] text-white`}>+ Strength test</Link>
      </div>

      <Link to={block ? `/gym/rehab/blocks/${block.id}` : '/gym/rehab/blocks'} className="block mb-3">
        <Card className="active:bg-[#1a1a1a]">
          {!block && <><div className="font-semibold">No block running</div><div className="text-sm text-gray-400">Start one: 28 days, one thing changed.</div></>}
          {block && !block.over && (
            <>
              <div className="flex items-baseline gap-2">
                <span className="font-semibold flex-1 truncate">{block.name}</span>
                <span className="text-sm tabular-nums text-gray-300">{block.started ? `Day ${block.day} of ${block.length}` : `Starts ${fmtDay(data.blocks.find(b => b.id === block.id)?.start_date)}`}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#232323] mt-2 overflow-hidden"><div className="h-full bg-green-500" style={{ width: `${(block.day / block.length) * 100}%` }} /></div>
              {data.blocks.find(b => b.id === block.id)?.variable && <div className="text-sm text-gray-400 mt-2 truncate">Changing: {data.blocks.find(b => b.id === block.id).variable}</div>}
            </>
          )}
          {block?.over && <><div className="font-semibold text-green-400">{block.name} is done</div><div className="text-sm text-gray-300">See how it went, write a conclusion and start the next one ›</div></>}
        </Card>
      </Link>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <Card>
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Mono half crimp</div>
          <div className="text-4xl font-bold tabular-nums mt-1">{a.headline.mono_half ? `${num(a.headline.mono_half.ratio)}%` : '–'}</div>
          <div className="text-xs text-gray-500 mt-0.5">{a.headline.mono_half ? `right ÷ left · ${fmtDay(a.headline.mono_half.date, { day: 'numeric', month: 'short' })}` : 'test both hands to see it'}</div>
        </Card>
        <Card>
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Mono drag</div>
          <div className="text-4xl font-bold tabular-nums mt-1 text-gray-200">{a.headline.mono_drag ? `${num(a.headline.mono_drag.ratio)}%` : '–'}</div>
          <div className="text-xs text-gray-500 mt-0.5">{a.headline.mono_drag ? `right ÷ left · ${fmtDay(a.headline.mono_drag.date, { day: 'numeric', month: 'short' })}` : 'test both hands to see it'}</div>
        </Card>
      </div>

      <Link to="/gym/rehab/charts" className="block mb-3">
        <Card className="active:bg-[#1a1a1a]">
          <div className="flex items-baseline mb-2">
            <span className="text-[11px] uppercase tracking-wide text-gray-500 flex-1">7-day average, last 4 weeks</span>
            <span className="text-xs text-gray-500">Charts ›</span>
          </div>
          <Sparkline analysis={a} today={today} />
        </Card>
      </Link>

      {recent.length > 0 && (
        <div className="mb-3">
          <Label>Recent</Label>
          <div className="space-y-1.5">
            {recent.map(r => (
              <Link key={`${r.kind}${r.id}`} to={`/gym/rehab/${r.kind}/${r.id}`} className="flex items-baseline gap-2 rounded-xl bg-[#141414] border border-[#232323] px-3 py-2 active:bg-[#1a1a1a]">
                <span className="text-xs text-gray-500 w-20 shrink-0">{fmtDay(r.date)}</span>
                <span className="flex-1 min-w-0"><span className="block truncate text-sm font-semibold">{r.title}</span><span className="block truncate text-xs text-gray-400">{r.sub}</span></span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 justify-center text-sm text-gray-400 pt-2">
        <Link to="/gym/rehab/blocks" className="px-2 py-1">Blocks</Link>
        <Link to="/gym/rehab/more" className="px-2 py-1">Reminder &amp; export</Link>
      </div>
    </>
  );
}

// --- Daily log ------------------------------------------------------------------------

const DAY_FIELDS = ['rest_pain', 'palm', 'post_pain', 'stiffness', 'provocative', 'knuckle_mm', 'climbed', 'notes'];
const emptyDay = () => ({ rest_pain: null, palm: null, post_pain: null, stiffness: null, provocative: null, knuckle_mm: null, climbed: null, notes: '' });

function DayLog() {
  const { data, reload } = useRehab();
  const navigate = useNavigate();
  const params = useParams();
  const date = params.date || data.analysis.today;
  const existing = data.days.find(d => d.date === date);
  const before = data.days.filter(d => d.date < date).at(-1);
  const [form, setForm] = useState(() => (existing ? Object.fromEntries(DAY_FIELDS.map(f => [f, existing[f]])) : emptyDay()));
  const [showPost, setShowPost] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Another day picked: show what that day has.
  useEffect(() => {
    const e = data.days.find(d => d.date === date);
    setForm(e ? Object.fromEntries(DAY_FIELDS.map(f => [f, e[f]])) : emptyDay());
    setShowPost(false);
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const trained = form.climbed === true || data.sessions.some(s => s.date === date) || form.post_pain != null || showPost;
  const filled = ['rest_pain', 'palm', 'stiffness', 'provocative'].filter(k => form[k] != null).length;

  async function save() {
    setBusy(true);
    setError('');
    try {
      await rehabAPI.saveDay(date, { ...form, climbed: form.climbed === true, notes: form.notes || '' });
      await reload();
      navigate('/gym/rehab');
    } catch (err) {
      setError(failText(err));
      setBusy(false);
    }
  }
  async function remove() {
    if (!window.confirm(`Delete the log for ${fmtDay(date)}?`)) return;
    await rehabAPI.deleteDay(date).catch(() => {});
    await reload();
    navigate('/gym/rehab');
  }

  return (
    <div className="pb-24">
      <Header title={date === data.analysis.today ? 'Today' : fmtDay(date)} back="/gym/rehab" />
      <div className="flex items-center gap-2 mb-2 -mt-1">
        <input type="date" value={date} max={data.analysis.today} onChange={e => e.target.value && navigate(`/gym/rehab/day/${e.target.value}`, { replace: true })} className={`${INPUT} !w-auto text-sm`} />
        <span className="text-xs text-gray-500">{existing ? 'Changing this day\'s log' : 'New log'}</span>
      </div>
      <p className="text-[11px] text-gray-600 mb-2">{before ? `The dot is what you logged on ${fmtDay(before.date)}.` : 'Tap a number or drag along a row.'}</p>

      <div className="space-y-1">
        <PainSlider label="Finger pain at rest" value={form.rest_pain} onChange={set('rest_pain')} ghost={before?.rest_pain} />
        <PainSlider label="Palm tenderness" value={form.palm} onChange={set('palm')} ghost={before?.palm} />
        <PainSlider label="Morning stiffness" value={form.stiffness} onChange={set('stiffness')} ghost={before?.stiffness} />
        <PainSlider label="Provocative test" hint="same half crimp every day" value={form.provocative} onChange={set('provocative')} ghost={before?.provocative} />
      </div>

      <div className="mt-3">
        <Label>Climbed today?</Label>
        <Segmented options={[['yes', 'Yes'], ['no', 'No']]} value={form.climbed === true ? 'yes' : form.climbed === false ? 'no' : null} onChange={v => set('climbed')(v === 'yes')} />
      </div>

      {trained
        ? <div className="mt-2"><PainSlider label="Pain after climbing or training" optional value={form.post_pain} onChange={set('post_pain')} ghost={before?.post_pain} /></div>
        : <button type="button" onClick={() => setShowPost(true)} className="mt-2 text-sm text-green-400 py-1">+ Pain after training</button>}

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <Label>Knuckle</Label>
          <NumberInput value={form.knuckle_mm} onChange={set('knuckle_mm')} suffix="mm" placeholder={before?.knuckle_mm != null ? `last ${num(before.knuckle_mm)}` : 'optional'} />
        </div>
      </div>
      <div className="mt-3">
        <Label>Notes</Label>
        <textarea value={form.notes || ''} onChange={e => set('notes')(e.target.value)} rows={2} placeholder="Optional" className={INPUT} />
      </div>
      {existing && <button onClick={remove} className="mt-4 text-sm text-red-400 py-1">Delete this day's log</button>}

      <SaveBar onSave={save} busy={busy} error={error} disabled={!filled && form.knuckle_mm == null && form.post_pain == null && !form.notes} label={existing ? 'Save changes' : 'Save today\'s log'} />
    </div>
  );
}

// --- Rehab session --------------------------------------------------------------------

/** The best force for this exercise, hand and grip in the 7 days before, and how far over it this is. Same as the server's. */
function forceJump(sessions, s) {
  if (s.force_kg == null) return null;
  const from = addDays(s.date, -7);
  const forces = sessions.filter(x => x.id !== s.id && x.exercise === s.exercise && x.hand === s.hand && (x.grip || null) === (s.grip || null) && x.date >= from && x.date <= s.date && x.force_kg != null).map(x => x.force_kg);
  if (!forces.length) return null;
  const best = Math.max(...forces);
  const over = (s.force_kg - best) / best;
  return best > 0 && over > 0.1 ? { best, percent: Math.round(over * 100) } : null;
}

const blankSession = (data) => {
  const last = data.sessions.at(-1);
  const exercise = data.exercises.find(e => e.name === last?.exercise) || data.exercises[0];
  return { date: data.analysis.today, exercise: exercise?.name || '', hand: last?.hand || 'right', grip: exercise?.uses_grip ? (last?.grip || 'half') : null, target_pain: exercise?.target_pain ?? 3, force_kg: null, sets: [], pain_during: null, notes: '' };
};

function SessionLog() {
  const { data, reload } = useRehab();
  const navigate = useNavigate();
  const { id } = useParams();
  const existing = id ? data.sessions.find(s => s.id === id) : null;
  const [form, setForm] = useState(() => (existing ? { ...existing, sets: existing.sets || [] } : blankSession(data)));
  const [adding, setAdding] = useState(false);
  const [editingList, setEditingList] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (id && !existing) return <Navigate to="/gym/rehab" replace />;

  const exercise = data.exercises.find(e => e.name === form.exercise);
  const usesGrip = exercise ? exercise.uses_grip : form.grip != null;
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const previous = data.sessions.filter(s => s.id !== form.id && s.exercise === form.exercise && s.hand === form.hand && (s.grip || null) === (usesGrip ? form.grip : null)).at(-1);
  const jump = forceJump(data.sessions, { ...form, grip: usesGrip ? form.grip : null });

  function pickExercise(e) {
    setForm(f => ({ ...f, exercise: e.name, target_pain: e.target_pain, grip: e.uses_grip ? (f.grip || 'half') : null }));
  }
  function repeat() {
    setForm(f => ({ ...f, target_pain: previous.target_pain, force_kg: previous.force_kg, sets: (previous.sets || []).map(r => ({ ...r })) }));
  }
  const setRow = (i, k, v) => setForm(f => ({ ...f, sets: f.sets.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));

  async function save() {
    if (!form.exercise) { setError('Pick an exercise.'); return; }
    setBusy(true);
    setError('');
    const body = {
      date: form.date, exercise: form.exercise, hand: form.hand, grip: usesGrip ? form.grip : null,
      target_pain: form.target_pain ?? 3, force_kg: form.force_kg, pain_during: form.pain_during, notes: form.notes || '',
      sets: form.sets.filter(r => r.sets).map(r => ({ sets: r.sets, seconds: r.seconds ?? null, kg: r.kg ?? null })),
    };
    try {
      if (existing) await rehabAPI.updateSession(existing.id, body); else await rehabAPI.createSession(body);
      await reload();
      navigate('/gym/rehab');
    } catch (err) {
      setError(failText(err));
      setBusy(false);
    }
  }
  async function remove() {
    if (!window.confirm('Delete this session?')) return;
    await rehabAPI.deleteSession(existing.id).catch(() => {});
    await reload();
    navigate('/gym/rehab');
  }

  return (
    <div className="pb-24">
      <Header title={existing ? 'Session' : 'New session'} back="/gym/rehab" />
      <input type="date" value={form.date} max={data.analysis.today} onChange={e => e.target.value && set('date')(e.target.value)} className={`${INPUT} !w-auto text-sm mb-3 -mt-1`} />

      <Label right={<button type="button" onClick={() => setEditingList(v => !v)} className="text-xs text-gray-400 px-1">{editingList ? 'Done' : 'Edit list'}</button>}>Exercise</Label>
      <div className="flex flex-wrap gap-2 mb-1">
        {data.exercises.map(e => (
          <span key={e.id} className="relative">
            <button type="button" onClick={() => pickExercise(e)} className={`rounded-xl px-3 py-2 text-sm text-left border ${form.exercise === e.name ? 'bg-green-500 text-black border-green-500 font-semibold' : 'bg-[#141414] border-[#2a2a2a] text-gray-200'}`}>{e.name}</button>
            {editingList && (
              <button type="button" aria-label={`Remove ${e.name}`} onClick={async () => { if (window.confirm(`Remove "${e.name}" from the list? Sessions already logged keep it.`)) { await rehabAPI.deleteExercise(e.id).catch(() => {}); reload(); } }} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-black text-sm font-bold">×</button>
            )}
          </span>
        ))}
        {!adding && <button type="button" onClick={() => setAdding(true)} className="rounded-xl px-3 py-2 text-sm border border-dashed border-[#3a3a3a] text-gray-400">+ New</button>}
      </div>
      {adding && <NewExercise onDone={async (e) => { setAdding(false); if (e) { await reload(); pickExercise(e); } }} />}

      <div className="mt-4 space-y-3">
        <div>
          <Label>Hand</Label>
          <Segmented options={[['left', 'Left'], ['right', 'Right (injured)']]} value={form.hand} onChange={set('hand')} />
        </div>
        {usesGrip && (
          <div>
            <Label>Grip</Label>
            <Segmented options={GRIPS} value={form.grip} onChange={set('grip')} />
          </div>
        )}
      </div>

      {previous && (
        <button type="button" onClick={repeat} className="mt-3 w-full rounded-xl border border-green-800 bg-green-500/10 px-4 py-2.5 text-left">
          <span className="block text-sm font-semibold text-green-300">↻ Repeat last session</span>
          <span className="block text-xs text-gray-400">{fmtDay(previous.date)}: {previous.force_kg != null ? `${num(previous.force_kg)} kg at ${previous.target_pain}/10` : `target ${previous.target_pain}/10`}{previous.sets?.length ? `, ${previous.sets.map(r => `${r.sets}×${r.seconds ?? '-'}s${r.kg != null ? ` @ ${num(r.kg)}` : ''}`).join(', ')}` : ''}</span>
        </button>
      )}

      <div className="mt-3">
        <PainSlider label="Target pain" hint={exercise ? 'saved for this exercise' : ''} value={form.target_pain} onChange={set('target_pain')} />
      </div>

      <div className="mt-3">
        <Label>Force at target pain</Label>
        <NumberInput value={form.force_kg} onChange={set('force_kg')} suffix="kg" placeholder="from the ramp-up pulls" />
        {jump && (
          <p className="text-sm text-amber-300 mt-1.5" role="alert">⚠️ {jump.percent}% above your best in the last 7 days ({num(jump.best)} kg). Check the reading, and that the pain really was {form.target_pain}/10.</p>
        )}
      </div>

      <div className="mt-4">
        <Label>Working sets</Label>
        {form.sets.length > 0 && (
          <div className="space-y-2 mb-2">
            {form.sets.map((r, i) => {
              const pct = r.kg != null && form.force_kg ? Math.round((r.kg / form.force_kg) * 100) : null;
              return (
                <div key={i} className="flex items-center gap-1.5">
                  <div className="w-12"><NumberInput value={r.sets} decimals={0} onChange={v => setRow(i, 'sets', v)} placeholder="sets" /></div>
                  <span className="text-gray-500 text-sm">×</span>
                  <div className="w-[4.75rem]"><NumberInput value={r.seconds} decimals={0} onChange={v => setRow(i, 'seconds', v)} suffix="s" /></div>
                  <span className="text-gray-500 text-sm">@</span>
                  <div className="flex-1 min-w-0"><NumberInput value={r.kg} onChange={v => setRow(i, 'kg', v)} suffix="kg" /></div>
                  <span className="w-10 text-right text-sm tabular-nums text-gray-300">{pct != null ? `${pct}%` : ''}</span>
                  <button type="button" aria-label="Remove these sets" onClick={() => setForm(f => ({ ...f, sets: f.sets.filter((_, j) => j !== i) }))} className="text-gray-500 px-1.5 text-lg">×</button>
                </div>
              );
            })}
            {form.force_kg == null && <p className="text-[11px] text-gray-500">Put in the force at target pain to see each load as a % of it.</p>}
          </div>
        )}
        <button type="button" onClick={() => setForm(f => ({ ...f, sets: [...f.sets, { ...(f.sets.at(-1) || { sets: 3, seconds: 10, kg: null }) }] }))} className="text-sm text-green-400 py-1">+ {form.sets.length ? 'Another row' : 'Add working sets'}</button>
      </div>

      <div className="mt-3">
        <PainSlider label="Pain during" value={form.pain_during} onChange={set('pain_during')} optional />
      </div>
      <div className="mt-3">
        <Label>Notes</Label>
        <textarea value={form.notes || ''} onChange={e => set('notes')(e.target.value)} rows={2} placeholder="Optional" className={INPUT} />
      </div>
      {existing && <button onClick={remove} className="mt-4 text-sm text-red-400 py-1">Delete this session</button>}

      <SaveBar onSave={save} busy={busy} error={error} label={existing ? 'Save changes' : 'Save session'} />
    </div>
  );
}

function NewExercise({ onDone }) {
  const [name, setName] = useState('');
  const [usesGrip, setUsesGrip] = useState(false);
  const [error, setError] = useState('');
  async function add() {
    if (!name.trim()) return;
    try {
      const r = await rehabAPI.createExercise({ name: name.trim(), uses_grip: usesGrip });
      onDone(r.data);
    } catch (err) {
      setError(failText(err));
    }
  }
  return (
    <div className="rounded-xl border border-[#2a2a2a] p-3 space-y-2 mt-2">
      <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Exercise name" className={INPUT} />
      <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={usesGrip} onChange={e => setUsesGrip(e.target.checked)} className="w-5 h-5 accent-green-500" /> A no-hang in a grip position (drag, intermediate, half crimp)</label>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={() => onDone(null)} className={`${BTN} flex-1 bg-[#1f1f1f] text-white text-sm`}>Cancel</button>
        <button type="button" onClick={add} disabled={!name.trim()} className={`${BTN} flex-1 bg-green-500 text-black text-sm`}>Add</button>
      </div>
    </div>
  );
}

// --- Strength test --------------------------------------------------------------------

const keyOf = (r) => `${r.test}|${r.grip}`;
const ratioOf = (r) => (r.left_kg > 0 && r.right_kg != null ? one((r.right_kg / r.left_kg) * 100) : null);

function TestLog() {
  const { data, reload } = useRehab();
  const navigate = useNavigate();
  const { id } = useParams();
  const existing = id ? data.tests.find(t => t.id === id) : null;
  const last = data.tests.at(-1);
  const [form, setForm] = useState(() => {
    if (existing) return { ...existing, results: existing.results || [], fixedOn: !!existing.fixed, fixed: existing.fixed || { test: 'mono', grip: 'half', load_kg: null, left_pain: null, right_pain: null } };
    const types = last?.results?.length ? last.results.map(r => ({ test: r.test, grip: r.grip })) : [{ test: 'mono', grip: 'half' }];
    return {
      date: data.analysis.today, edge_mm: last?.edge_mm ?? null, target_pain: last?.target_pain ?? 3, notes: '',
      results: types.map(t => ({ ...t, left_kg: null, right_kg: null, left_pain: null, right_pain: null })),
      fixedOn: !!last?.fixed,
      fixed: last?.fixed ? { ...last.fixed, left_pain: null, right_pain: null } : { test: 'mono', grip: 'half', load_kg: 5, left_pain: null, right_pain: null },
    };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (id && !existing) return <Navigate to="/gym/rehab" replace />;

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const chosen = new Set(form.results.map(keyOf));
  function toggle(tt) {
    setForm(f => (chosen.has(keyOf(tt))
      ? { ...f, results: f.results.filter(r => keyOf(r) !== keyOf(tt)) }
      : { ...f, results: [...f.results, { test: tt.test, grip: tt.grip, left_kg: null, right_kg: null, left_pain: null, right_pain: null }].sort((a, b) => TEST_TYPES.findIndex(t => keyOf(t) === keyOf(a)) - TEST_TYPES.findIndex(t => keyOf(t) === keyOf(b))) }));
  }
  const setResult = (k, field, v) => setForm(f => ({ ...f, results: f.results.map(r => (keyOf(r) === k ? { ...r, [field]: v } : r)) }));
  const setFixed = (field, v) => setForm(f => ({ ...f, fixed: { ...f.fixed, [field]: v } }));
  const previousFor = (r) => data.analysis.ratios.filter(x => x.test === r.test && x.grip === r.grip && x.date < form.date).at(-1);

  async function save() {
    const logged = form.results.filter(r => r.left_kg != null || r.right_kg != null);
    if (!logged.length && !(form.fixedOn && form.fixed.load_kg != null)) { setError('Put in a force for at least one test.'); return; }
    const oneHanded = logged.filter(r => r.left_kg == null || r.right_kg == null);
    if (oneHanded.length && !window.confirm(`${oneHanded.map(r => testLabel(r.test, r.grip)).join(', ')}: only one hand is in, so there's no ratio for it. Save anyway?`)) return;
    setBusy(true);
    setError('');
    const body = {
      date: form.date, edge_mm: form.edge_mm, target_pain: form.target_pain ?? 3, notes: form.notes || '',
      results: logged.map(r => ({ test: r.test, grip: r.grip, left_kg: r.left_kg, right_kg: r.right_kg, left_pain: r.left_pain, right_pain: r.right_pain })),
      fixed: form.fixedOn && form.fixed.load_kg != null ? form.fixed : null,
    };
    try {
      if (existing) await rehabAPI.updateTest(existing.id, body); else await rehabAPI.createTest(body);
      await reload();
      navigate('/gym/rehab');
    } catch (err) {
      setError(failText(err));
      setBusy(false);
    }
  }
  async function remove() {
    if (!window.confirm('Delete this strength test?')) return;
    await rehabAPI.deleteTest(existing.id).catch(() => {});
    await reload();
    navigate('/gym/rehab');
  }

  return (
    <div className="pb-24">
      <Header title={existing ? 'Strength test' : 'Weekly strength test'} back="/gym/rehab" />
      <p className="text-sm text-gray-400 -mt-1 mb-3">Max force for a 10-second hold at the target pain. Both hands, one after the other.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Date</Label><input type="date" value={form.date} max={data.analysis.today} onChange={e => e.target.value && set('date')(e.target.value)} className={`${INPUT} text-sm`} /></div>
        <div><Label>Edge</Label><NumberInput value={form.edge_mm} onChange={set('edge_mm')} suffix="mm" /></div>
      </div>
      <div className="mt-2"><PainSlider label="Target pain" value={form.target_pain} onChange={set('target_pain')} /></div>

      <div className="mt-4">
        <Label>Tests</Label>
        <div className="flex flex-wrap gap-1.5">
          {TEST_TYPES.map(tt => (
            <button key={keyOf(tt)} type="button" onClick={() => toggle(tt)} className={`rounded-full px-3 py-1.5 text-xs border ${chosen.has(keyOf(tt)) ? 'bg-[#2a2a2a] border-[#444] text-white font-semibold' : 'border-[#2a2a2a] text-gray-500'}`}>{chosen.has(keyOf(tt)) ? '✓ ' : ''}{tt.label}</button>
          ))}
        </div>
      </div>

      <div className="space-y-3 mt-3">
        {form.results.map(r => {
          const k = keyOf(r);
          const ratio = ratioOf(r);
          const prev = previousFor(r);
          return (
            <Card key={k} className="!p-3">
              <div className="flex items-baseline mb-2">
                <span className="font-semibold flex-1">{testLabel(r.test, r.grip)}</span>
                <span className="text-right">
                  <span className={`block text-lg font-bold tabular-nums ${ratio == null ? 'text-gray-600' : 'text-white'}`}>{ratio == null ? '–' : `${num(ratio)}%`}</span>
                  {prev?.ratio != null && <span className="block text-[11px] text-gray-500">last {num(prev.ratio)}%</span>}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[['left', 'Left'], ['right', 'Right (injured)']].map(([h, label]) => (
                  <div key={h}>
                    <Label>{label}</Label>
                    <NumberInput value={r[`${h}_kg`]} onChange={v => setResult(k, `${h}_kg`, v)} suffix="kg" placeholder={prev?.[`${h}_kg`] != null ? `last ${num(prev[`${h}_kg`])}` : ''} />
                  </div>
                ))}
              </div>
              <div className="mt-2 space-y-1">
                <PainSlider compact label="Left pain" value={r.left_pain} onChange={v => setResult(k, 'left_pain', v)} optional />
                <PainSlider compact label="Right pain" value={r.right_pain} onChange={v => setResult(k, 'right_pain', v)} optional />
              </div>
            </Card>
          );
        })}
      </div>

      <div className="mt-4">
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={form.fixedOn} onChange={e => set('fixedOn')(e.target.checked)} className="w-5 h-5 accent-green-500" /> Fixed-load test</label>
        {form.fixedOn && (
          <Card className="!p-3 mt-2 space-y-2">
            <p className="text-xs text-gray-500">The same load on each hand; how much it hurts. Should come down as the force at target pain goes up.</p>
            <div className="grid grid-cols-[1fr_7rem] gap-2">
              <select value={`${form.fixed.test}|${form.fixed.grip}`} onChange={e => { const [test, grip] = e.target.value.split('|'); setForm(f => ({ ...f, fixed: { ...f.fixed, test, grip } })); }} className={`${INPUT} text-sm`}>
                {TEST_TYPES.map(tt => <option key={keyOf(tt)} value={keyOf(tt)}>{tt.label}</option>)}
              </select>
              <NumberInput value={form.fixed.load_kg} onChange={v => setFixed('load_kg', v)} suffix="kg" />
            </div>
            <div className="space-y-1">
              <PainSlider compact label="Left pain" value={form.fixed.left_pain} onChange={v => setFixed('left_pain', v)} />
              <PainSlider compact label="Right pain" value={form.fixed.right_pain} onChange={v => setFixed('right_pain', v)} />
            </div>
          </Card>
        )}
      </div>

      <div className="mt-3"><Label>Notes</Label><textarea value={form.notes || ''} onChange={e => set('notes')(e.target.value)} rows={2} placeholder="Optional" className={INPUT} /></div>
      {existing && <button onClick={remove} className="mt-4 text-sm text-red-400 py-1">Delete this test</button>}

      <SaveBar onSave={save} busy={busy} error={error} label={existing ? 'Save changes' : 'Save test'} />
    </div>
  );
}

// --- Blocks ---------------------------------------------------------------------------

function Blocks() {
  const { data } = useRehab();
  const statusOf = (id) => data.analysis.blocks.find(b => b.id === id);
  const open = data.blocks.find(b => !b.closed_at);
  const list = data.blocks.slice().reverse();
  return (
    <>
      <Header title="Blocks" back="/gym/rehab" />
      <p className="text-sm text-gray-400 -mt-1 mb-3">An experiment: change one thing for a set time, then compare the first week with the last.</p>
      {!open && <Link to="/gym/rehab/blocks/new" className={`${BTN} block text-center bg-green-500 text-black mb-4`}>Start a block</Link>}
      <div className="space-y-2">
        {list.map(b => {
          const s = statusOf(b.id);
          return (
            <Link key={b.id} to={`/gym/rehab/blocks/${b.id}`} className="block rounded-2xl bg-[#141414] border border-[#232323] px-4 py-3 active:bg-[#1a1a1a]">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold flex-1 truncate">{b.name}</span>
                <span className="text-xs text-gray-400">{b.closed_at ? 'Concluded' : s?.over ? 'Finished: conclude it' : s?.started ? `Day ${s.day} of ${s.length}` : 'Not started'}</span>
              </div>
              <div className="text-sm text-gray-400 truncate">{fmtDay(b.start_date, { day: 'numeric', month: 'short', year: 'numeric' })} · {b.length_days} days{b.variable ? ` · ${b.variable}` : ''}</div>
            </Link>
          );
        })}
        {!list.length && <p className="text-gray-500 text-sm">No blocks yet.</p>}
      </div>
    </>
  );
}

function BlockFields({ form, set }) {
  return (
    <div className="space-y-3">
      <div><Label>Name</Label><input value={form.name} onChange={e => set('name')(e.target.value)} placeholder="Block 1" className={INPUT} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Start</Label><input type="date" value={form.start_date} onChange={e => e.target.value && set('start_date')(e.target.value)} className={`${INPUT} text-sm`} /></div>
        <div><Label>Length</Label><NumberInput value={form.length_days} decimals={0} onChange={set('length_days')} suffix="days" /></div>
      </div>
      <div><Label>The one thing being changed</Label><input value={form.variable} onChange={e => set('variable')(e.target.value)} placeholder="e.g. mono no-hangs daily instead of every other day" className={INPUT} /></div>
      <div><Label>Notes</Label><textarea value={form.notes} onChange={e => set('notes')(e.target.value)} rows={3} placeholder="Anything else about this block" className={INPUT} /></div>
    </div>
  );
}

function BlockForm() {
  const { data, reload } = useRehab();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [form, setForm] = useState({ name: `Block ${data.blocks.length + 1}`, start_date: data.analysis.today, length_days: 28, variable: search.get('variable') || '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  if (data.blocks.some(b => !b.closed_at)) return <Navigate to="/gym/rehab/blocks" replace />;
  async function save() {
    setBusy(true);
    setError('');
    try {
      const r = await rehabAPI.createBlock({ ...form, name: form.name.trim() || `Block ${data.blocks.length + 1}`, length_days: form.length_days || 28 });
      await reload();
      navigate(`/gym/rehab/blocks/${r.data.id}`, { replace: true });
    } catch (err) {
      setError(failText(err));
      setBusy(false);
    }
  }
  return (
    <div className="pb-24">
      <Header title="New block" back="/gym/rehab/blocks" />
      <BlockFields form={form} set={set} />
      <SaveBar onSave={save} busy={busy} error={error} label="Start block" />
    </div>
  );
}

function BlockPage() {
  const { data, reload } = useRehab();
  const navigate = useNavigate();
  const { id } = useParams();
  const block = data.blocks.find(b => b.id === id);
  const status = data.analysis.blocks.find(b => b.id === id);
  const [form, setForm] = useState(() => (block ? { name: block.name, start_date: block.start_date, length_days: block.length_days, variable: block.variable, notes: block.notes } : null));
  const [editing, setEditing] = useState(false);
  const [conclusion, setConclusion] = useState(block?.conclusion || '');
  const [note, setNote] = useState({ date: data.analysis.today, text: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!block) return <Navigate to="/gym/rehab/blocks" replace />;
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const markers = (block.markers || []).slice().sort((a, b) => a.date.localeCompare(b.date));

  async function patch(body, after) {
    setBusy(true);
    setError('');
    try {
      await rehabAPI.updateBlock(block.id, body);
      await reload();
      after?.();
    } catch (err) {
      setError(failText(err));
    } finally {
      setBusy(false);
    }
  }
  const addNote = () => note.text.trim() && patch({ markers: [...markers, { id: newId(), date: note.date, text: note.text.trim() }] }, () => setNote(n => ({ ...n, text: '' })));
  const removeNote = (mid) => patch({ markers: markers.filter(m => m.id !== mid) });
  async function remove() {
    if (!window.confirm(`Delete "${block.name}"? Your logs stay; only the block goes.`)) return;
    await rehabAPI.deleteBlock(block.id).catch(() => {});
    await reload();
    navigate('/gym/rehab/blocks', { replace: true });
  }

  return (
    <div className="pb-10">
      <Header title={block.name} back="/gym/rehab/blocks" right={!editing && <button onClick={() => setEditing(true)} className="text-sm text-gray-400 px-2 py-1">Edit</button>} />
      {error && <p className="text-sm text-red-400 mb-2">{error}</p>}

      {editing ? (
        <Card className="mb-4">
          <BlockFields form={form} set={set} />
          <div className="flex gap-2 mt-3">
            <button onClick={() => setEditing(false)} className={`${BTN} flex-1 bg-[#1f1f1f] text-white text-sm`}>Cancel</button>
            <button disabled={busy} onClick={() => patch({ ...form, length_days: form.length_days || 28 }, () => setEditing(false))} className={`${BTN} flex-1 bg-green-500 text-black text-sm`}>Save</button>
          </div>
          <button onClick={remove} className="mt-3 text-sm text-red-400 py-1">Delete this block</button>
        </Card>
      ) : (
        <Card className="mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-gray-400 flex-1">{fmtDay(block.start_date, { day: 'numeric', month: 'short' })} to {fmtDay(status.end_date, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            <span className="text-sm font-semibold tabular-nums">{block.closed_at ? 'Concluded' : status.over ? 'Finished' : status.started ? `Day ${status.day} of ${status.length}` : 'Not started'}</span>
          </div>
          {!block.closed_at && status.started && <div className="h-1.5 rounded-full bg-[#232323] mt-2 overflow-hidden"><div className="h-full bg-green-500" style={{ width: `${(status.day / status.length) * 100}%` }} /></div>}
          {block.variable && <div className="mt-3"><Label>Changing</Label><div>{block.variable}</div></div>}
          {block.notes && <div className="mt-3"><Label>Notes</Label><div className="text-sm text-gray-300 whitespace-pre-wrap">{block.notes}</div></div>}
        </Card>
      )}

      <Label>Dated notes</Label>
      <p className="text-[11px] text-gray-500 mb-2">Anything started or stopped (a supplement, a new exercise, a week off). Each shows as a marker on the charts.</p>
      <div className="space-y-1.5 mb-2">
        {markers.map(m => (
          <div key={m.id} className="flex items-center gap-2 rounded-xl bg-[#141414] border border-[#232323] px-3 py-2">
            <span className="text-xs text-gray-500 w-16 shrink-0">{fmtDay(m.date, { day: 'numeric', month: 'short' })}</span>
            <span className="flex-1 text-sm">{m.text}</span>
            <button aria-label="Remove this note" onClick={() => removeNote(m.id)} className="text-gray-500 px-1.5">×</button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-5">
        <input type="date" value={note.date} onChange={e => e.target.value && setNote(n => ({ ...n, date: e.target.value }))} className={`${INPUT} !w-36 text-sm`} />
        <input value={note.text} onChange={e => setNote(n => ({ ...n, text: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addNote()} placeholder="Started creatine" className={INPUT} />
        <button onClick={addNote} disabled={busy || !note.text.trim()} className={`${BTN} !px-3 bg-[#1f1f1f] text-white text-sm`}>Add</button>
      </div>

      {status.started && <BlockSummary summary={status.summary} partial={!status.over && !block.closed_at} />}

      {(status.over || block.closed_at) && (
        <div className="mt-5">
          <Label>Conclusion</Label>
          <textarea value={conclusion} onChange={e => setConclusion(e.target.value)} rows={4} placeholder="What did this block show? What changes next?" className={INPUT} />
          {block.closed_at ? (
            <button disabled={busy || conclusion === block.conclusion} onClick={() => patch({ conclusion })} className={`${BTN} w-full mt-2 bg-[#1f1f1f] text-white`}>Save conclusion</button>
          ) : (
            <button
              disabled={busy || !conclusion.trim()}
              onClick={() => patch({ conclusion, closed: true }, () => navigate('/gym/rehab/blocks/new', { replace: true }))}
              className={`${BTN} w-full mt-2 bg-green-500 text-black`}
            >Conclude and start the next block</button>
          )}
        </div>
      )}
    </div>
  );
}

const PAIN_LABELS = Object.fromEntries(PAIN_SERIES.map(s => [s.key, s.label]));

/** An arrow and colour that say better or worse, never colour alone. */
function Change({ c, unit = '' }) {
  if (!c || c.change == null) return <span className="text-gray-600">–</span>;
  const arrow = c.change > 0 ? '↑' : c.change < 0 ? '↓' : '→';
  const tone = c.better == null ? 'text-gray-400' : c.better ? 'text-green-400' : 'text-red-400';
  return <span className={`tabular-nums font-semibold ${tone}`}>{arrow} {num(Math.abs(c.change))}{unit}{c.better != null && <span className="sr-only">{c.better ? ' better' : ' worse'}</span>}</span>;
}

export function BlockSummary({ summary, partial }) {
  const row = (label, c, unit = '', sub) => (
    <div key={label} className="grid grid-cols-[1fr_3.5rem_3.5rem_4.5rem] gap-1 items-baseline py-1.5 border-t border-[#232323] text-sm">
      <span className="min-w-0"><span className="block truncate">{label}</span>{sub && <span className="block text-[11px] text-gray-500 truncate">{sub}</span>}</span>
      <span className="tabular-nums text-right text-gray-300">{c.first == null ? '–' : num(c.first)}</span>
      <span className="tabular-nums text-right text-gray-300">{c.last == null ? '–' : num(c.last)}</span>
      <span className="text-right"><Change c={c} unit={unit} /></span>
    </div>
  );
  const head = (a, b) => (
    <div className="grid grid-cols-[1fr_3.5rem_3.5rem_4.5rem] gap-1 text-[10px] uppercase tracking-wide text-gray-500 pb-1">
      <span /><span className="text-right">{a}</span><span className="text-right">{b}</span><span className="text-right">Change</span>
    </div>
  );
  return (
    <Card>
      <div className="font-semibold">{partial ? 'So far' : 'How it went'}</div>
      <p className="text-xs text-gray-500 mb-3">{partial ? 'The final week isn\'t here yet; this fills in as it is.' : 'Week 1 against the final week, averaged over the days you logged. Green is better, red is worse.'}</p>
      {head('Week 1', 'Final')}
      {Object.entries(summary.pain).map(([k, c]) => row(PAIN_LABELS[k], c, '', `${c.days_first} and ${c.days_last} days logged`))}
      {row('Knuckle', summary.circumference, ' mm')}
      {summary.force.length > 0 && <div className="mt-3">{head('Week 1', 'Final')}{summary.force.map(f => row(f.exercise, f, ' kg', `Force at target pain · ${f.hand === 'right' ? 'right' : 'left'}${f.grip ? `, ${gripLabel(f.grip).toLowerCase()}` : ''}`))}</div>}
      {summary.strength.length > 0 && (
        <div className="mt-3">
          {head('First', 'Last')}
          {summary.strength.map(s => (
            <React.Fragment key={`${s.test}${s.grip}`}>
              {s.ratio && row(`${s.label} ratio`, s.ratio, '%', `${fmtDay(s.ratio.from, { day: 'numeric', month: 'short' })} to ${fmtDay(s.ratio.to, { day: 'numeric', month: 'short' })}`)}
              {s.right_kg && row(`${s.label}, right`, s.right_kg, ' kg')}
            </React.Fragment>
          ))}
        </div>
      )}
      {!summary.strength.length && <p className="text-xs text-gray-500 mt-3">Strength changes show once there are two tests with both hands in the block.</p>}
    </Card>
  );
}

// --- Reminder and export ----------------------------------------------------------------

const icsDate = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
// The reminder repeats daily, so it is written in local ("floating") time: 08:30 stays 08:30 when the clocks change.
const icsLocal = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
const REMINDER_KEY = 'gym.rehab.reminder';

function download(name, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const cell = (v) => {
  if (v == null) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csv = (cols, rows) => [cols.join(','), ...rows.map(r => cols.map(c => cell(r[c])).join(','))].join('\r\n');

export function exportCsv(data) {
  const daily = csv(['date', 'rest_pain', 'palm', 'post_pain', 'stiffness', 'provocative', 'knuckle_mm', 'climbed', 'notes'], data.days);
  const sessions = csv(['date', 'exercise', 'hand', 'grip', 'target_pain', 'force_kg', 'sets', 'pain_during', 'notes'],
    data.sessions.map(s => ({ ...s, sets: (s.sets || []).map(r => `${r.sets}x${r.seconds ?? ''}s@${r.kg ?? ''}kg`).join(' ; ') })));
  const tests = csv(['date', 'edge_mm', 'target_pain', 'test', 'grip', 'left_kg', 'right_kg', 'ratio_pct', 'left_pain', 'right_pain', 'notes'],
    data.tests.flatMap(t => [
      ...(t.results || []).map(r => ({ ...t, ...r, ratio_pct: ratioOf(r) })),
      ...(t.fixed ? [{ ...t, test: `fixed ${num(t.fixed.load_kg)} kg ${t.fixed.test}`, grip: t.fixed.grip, left_pain: t.fixed.left_pain, right_pain: t.fixed.right_pain }] : []),
    ]));
  const blocks = csv(['name', 'start_date', 'length_days', 'variable', 'notes', 'markers', 'conclusion', 'closed_at'],
    data.blocks.map(b => ({ ...b, markers: (b.markers || []).map(m => `${m.date}: ${m.text}`).join(' ; ') })));
  return { daily, sessions, tests, blocks };
}

function More() {
  const { data } = useRehab();
  const [time, setTime] = useState(() => { try { return localStorage.getItem(REMINDER_KEY) || '08:30'; } catch { return '08:30'; } });
  useEffect(() => { try { localStorage.setItem(REMINDER_KEY, time); } catch { /* fine */ } }, [time]);
  const files = useMemo(() => exportCsv(data), [data]);
  const stamp = data.analysis.today;

  const start = new Date(`${data.analysis.today}T${time}:00`);
  const end = new Date(start.getTime() + 5 * 60000);
  const link = `${window.location.origin}/gym/rehab/day`;
  const title = 'Log your finger';
  function phoneCalendar() {
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Mission Control//Rehab//EN', 'BEGIN:VEVENT',
      `UID:${newId()}@missioncontrol.show`, `DTSTAMP:${icsDate(new Date())}`, `DTSTART:${icsLocal(start)}`, `DTEND:${icsLocal(end)}`,
      'RRULE:FREQ=DAILY', `SUMMARY:${title}`, `DESCRIPTION:Under a minute: ${link}`, `URL:${link}`,
      'BEGIN:VALARM', 'TRIGGER:PT0M', 'ACTION:DISPLAY', `DESCRIPTION:${title}`, 'END:VALARM',
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    download('Rehab reminder.ics', ics, 'text/calendar');
  }
  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${icsLocal(start)}/${icsLocal(end)}&ctz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London')}&recur=${encodeURIComponent('RRULE:FREQ=DAILY')}&details=${encodeURIComponent(link)}`;

  return (
    <>
      <Header title="Reminder & export" back="/gym/rehab" />
      <Card className="mb-4 space-y-3">
        <div className="font-semibold">⏰ Daily reminder</div>
        <p className="text-sm text-gray-400">A repeating event in your calendar that alerts you every day at this time, with a link straight to today's log. Your phone's calendar does the reminding, so it works even when this app is closed.</p>
        <div className="flex items-center gap-3">
          <input type="time" value={time} onChange={e => e.target.value && setTime(e.target.value)} className={`${INPUT} !w-32`} />
          <span className="text-sm text-gray-500">every day</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={phoneCalendar} className={`${BTN} bg-green-500 text-black text-sm`}>Phone calendar</button>
          <a href={google} target="_blank" rel="noreferrer" className={`${BTN} bg-[#1f1f1f] text-white text-sm text-center`}>Google Calendar</a>
        </div>
        <p className="text-[11px] text-gray-500">To change the time later, delete the old event in your calendar and add a new one.</p>
      </Card>

      <Card className="space-y-3">
        <div className="font-semibold">⬇️ Export as CSV</div>
        <p className="text-sm text-gray-400">Everything you've logged, in four files that open in Excel or Google Sheets.</p>
        <div className="grid grid-cols-2 gap-2">
          {[['daily', 'Daily log', data.days.length], ['sessions', 'Sessions', data.sessions.length], ['tests', 'Strength tests', data.tests.length], ['blocks', 'Blocks', data.blocks.length]].map(([k, label, n]) => (
            <button key={k} onClick={() => download(`Rehab ${label.toLowerCase()} ${stamp}.csv`, files[k], 'text/csv')} className={`${BTN} bg-[#1f1f1f] text-white text-sm`}>
              {label}<span className="block text-[11px] font-normal text-gray-500">{n} row{n === 1 ? '' : 's'}</span>
            </button>
          ))}
        </div>
      </Card>
    </>
  );
}
